/**
 * F-CTRL-N80 — the tenant's discount and service-charge controls, and the
 * till's copy of the server's rules (backend handlers/order_adjustments.go).
 *
 * The server is authoritative: it refuses a change that lacks a reason or a
 * manager PIN with a stable error code. These rules only decide when the till
 * ASKS, so a cashier sees the reason chips or the PIN pad before pressing
 * Apply rather than after a refusal. Same thresholds as the server (0.005
 * rupees, percent rounded to 2 dp); pinned by discountControls.test.ts.
 */

export const DISCOUNT_CONTROLS_KEY = 'discount_controls'

export interface DiscountControls {
  reasons_required: boolean
  /** A change taking the bill's total manual discount above this % of the subtotal needs a PIN; null = off. */
  pin_above_percent: number | null
  pin_to_waive_service_charge: boolean
}

export type AdjustmentReasonCode = 'regular' | 'owners_guest' | 'make_good' | 'staff' | 'promotion' | 'other'

/** The one-tap reasons, in the order the till shows them. */
export const ADJUSTMENT_REASONS: { code: AdjustmentReasonCode; label: string }[] = [
  { code: 'regular', label: 'Regular customer' },
  { code: 'owners_guest', label: "Owner's guest" },
  { code: 'make_good', label: 'Complaint / make-good' },
  { code: 'staff', label: 'Staff' },
  { code: 'promotion', label: 'Promotion' },
  { code: 'other', label: 'Other' },
]

export function adjustmentReasonLabel(code: string | null | undefined): string {
  return ADJUSTMENT_REASONS.find(r => r.code === code)?.label ?? 'No reason recorded'
}

const OFF: DiscountControls = { reasons_required: false, pin_above_percent: null, pin_to_waive_service_charge: false }

/** A stored value → controls. Anything but a valid object reads as all off, as on the server. */
export function parseDiscountControls(raw: unknown): DiscountControls {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...OFF }
  const r = raw as Record<string, unknown>
  const bool = (v: unknown) => v === undefined || typeof v === 'boolean'
  const pct = r.pin_above_percent
  if (!bool(r.reasons_required) || !bool(r.pin_to_waive_service_charge)) return { ...OFF }
  if (pct !== undefined && pct !== null && typeof pct !== 'number') return { ...OFF }
  return {
    reasons_required: r.reasons_required === true,
    pin_above_percent: typeof pct === 'number' && pct >= 1 && pct <= 100 ? pct : null,
    pin_to_waive_service_charge: r.pin_to_waive_service_charge === true,
  }
}

export interface AdjustmentNeed {
  reason: boolean
  pin: boolean
}

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * A manual discount change. totalBefore / totalAfter are the bill's whole
 * manual discount (bill + item discounts); only a change that raises it past
 * the limit needs a PIN, and clearing a discount never asks for anything.
 */
export function discountNeed(
  c: DiscountControls,
  a: { changed: boolean; elementAfter: number; subtotal: number; totalBefore: number; totalAfter: number },
): AdjustmentNeed {
  if (!a.changed) return { reason: false, pin: false }
  const reason = c.reasons_required && a.elementAfter > 0.005
  const pin =
    c.pin_above_percent != null &&
    a.totalAfter > a.totalBefore + 0.005 &&
    a.subtotal > 0 &&
    round2((a.totalAfter / a.subtotal) * 100) > c.pin_above_percent
  return { reason, pin }
}

/** A service-charge change: only a waiver of a charge the bill would carry asks. */
export function serviceChargeNeed(c: DiscountControls, a: { changed: boolean; atSetRate: number; after: number }): AdjustmentNeed {
  const waiver = a.changed && a.after < 0.005 && a.atSetRate > 0.005
  return { reason: c.reasons_required && waiver, pin: c.pin_to_waive_service_charge && waiver }
}

const REASON_REFUSALS = new Set(['adjustment_reason_required', 'adjustment_note_required'])
const PIN_REFUSALS = new Set(['manager_pin_required', 'invalid_manager_pin'])

/**
 * The server refused a change for want of a reason or a PIN — what the dialog
 * must ask for when it reopens. Keeps whatever step the last attempt already
 * carried. null for any other error, which stays a toast.
 */
export function adjustmentNeedFromRefusal(
  code: string | undefined,
  sent: { reason_code?: string; pin?: string } | undefined,
): AdjustmentNeed | null {
  if (code && REASON_REFUSALS.has(code)) return { reason: true, pin: !!sent?.pin }
  if (code && PIN_REFUSALS.has(code)) return { reason: !!sent?.reason_code, pin: true }
  return null
}

/** The refusal in the till's words. */
export function adjustmentRefusalMessage(code: string | undefined): string {
  switch (code) {
    case 'adjustment_reason_required':
      return 'Pick a reason for this.'
    case 'adjustment_note_required':
      return 'Add a few words for "Other".'
    case 'manager_pin_required':
      return 'A manager PIN is needed for this.'
    case 'invalid_manager_pin':
      return "That PIN isn't a manager's."
    default:
      return ''
  }
}
